import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { ConfirmationModal, Error, HelpModal, Input, Loadable, RichEditor, Spinner, Switch } from "../../common";
import { Button, Col, HelpText } from "../../App.styles";
import { NubImageUpload, RowInfo } from "../../common";
import { useCreateJudgeMutation, useDeleteJudgeMutation, useJudgeItemQuery, useJudgeQuery } from "./JudgingSection.state";

const JudgeItem = ({ taskId, id, judgeQuery }) => {
    const { t } = useTranslation()
    const useQuery = () => useJudgeItemQuery({ taskId, id, judgeQuery })
    const [collection] = useQuery()
    const { validity = '' } = collection || {}
    const { mutateAsync: deleteJudge, isLoading: isDeleting } = useDeleteJudgeMutation({ taskId, id })

    return (
        <>
            <RowInfo
                isFull
                help={(
                    <HelpModal title={t("Avatar for Judge")}>
                        <p>
                            {t(`Assign your judge a profile photo.`)}
                        </p>
                        <ul>
                            <li>{t(`For images, you can use any graphics application you want that 
                                allows you to export JPEG, GIF or PNG files.`)}</li>
                            <li>{t(`Submit the largest (resolution) size image you can and we will 
                                resize it as required.`)}</li>
                            <li>{t(`Always orient your image for portrait mode.`)}</li>
                        </ul>
                    </HelpModal>
                )}
            >
                <NubImageUpload name="media" label={t('Avatar')} useQuery={useQuery} standalone storageMode="legacy" />
            </RowInfo>

            <RowInfo help={(
                <HelpModal title={t(`Name`)}>
                    <p>{t(`Give your judge a name.`)}</p>
                    <ul>
                        <li>{t(`Players will see this name in communications.`)}</li>
                        <li>{t(`Use the same name across challenges or use a unique judge name for each challenge.`)}</li>
                    </ul>

                </HelpModal>
            )}>
                <Input
                    label={t('Name')}
                    mode="clean"
                    name="name"
                    useQuery={useQuery}
                />
            </RowInfo>

            <HelpText className="position-relative top-0 my-4 d-block" focus>
                <p dangerouslySetInnerHTML={{
                    __html: t(`Describe how you would like this challenge to be judged. You could give your judge 
                        a name, tell them to speak in pirate, or otherwise direct their personality. The judge has 
                        access to everything related to the submission - it’s location, the photo or video attached, 
                        the time submitted. Active configurations will be applied to incoming submissions for the game. 
                        `)
                }} />
                <p dangerouslySetInnerHTML={{
                    __html: t(`The judge will make a single verdict with the following attributes - correct/incorrect, 
                        greatness <br/>(0-100), point adjustment (+/-), and comment. Tell the judge how to determine each of 
                        those things in plain language. If your configuration is rejected, we will tell you why.`)
                }} />
            </HelpText>

            <RowInfo help={(
                <HelpModal title={t(`Instructions`)}>
                    <p>{t(`Tell your judge how to assess each submission to this challenge.`)}</p>
                    <ul>
                        <li>{t(`First, the instructions must tell the judge how to determine if the player's submission is correct or incorrect.`)}</li>
                        <li>{t(`Second, the instructions must tell the judge how to score a submission between 1 and 100 based on how great the submission is, enabling identification of the best.`)}</li>
                        <li>{t(`Third, the instructions must tell the judge how to award or penalize points for the submission, and guidance as to how many points.`)}</li>
                        <li>{t(`Lastly, the instructions should tell the judge how to comment on the submission for the entertainment of the players.`)}</li>
                    </ul>
                </HelpModal>
            )}>
                <RichEditor
                    label={t('Instructions')}
                    name="prompt"
                    useQuery={useQuery}
                />
            </RowInfo>

            <RowInfo help={(
                <HelpModal title={t(`Active Judging`)}>
                    <p>{t(`Is the judge enabled for incoming submissions?`)}</p>
                    <ul>
                        <li>{t(`If your instructions are not adequate, your judge will be disabled automatically. 
                            Reasons why are displayed beneath the toggle to help you provide adequate instructions.`)}</li>
                        <li>{t(`If your instructions are valid, similarly your judge will be enabled automatically.`)}</li>
                        <li>{t(`If you wish to disable judging, simply clear the instructions. It will be automatically disabled.`)}</li>
                    </ul>
                </HelpModal>
            )}>
                <Switch
                    label={t("Active Judging")}
                    labels={{ on: t("Enabled"), off: t("Disabled") }}
                    labelPosition="outside"
                    name="active"
                    on={1}
                    off={0}
                    useQuery={useQuery}
                    disabled
                />
            </RowInfo>

            <RowInfo rowClassName="mt-0">
                <Col size={12}>
                    {validity}
                </Col>
            </RowInfo>

            <RowInfo help={(
                <HelpModal title={t(`Communications`)}>
                    <p>{t(`Have your judge pass feedback privately or publicly.`)}</p>
                    <ul>
                        <li>{t(`If public communications are disabled, the judge will send them private chat messages.`)}</li>
                        <li>{t(`If public communications are enabled, the judge will add comments and likes to their submissions so everyone can see.`)}</li>
                    </ul>
                </HelpModal>
            )}>
                <Switch
                    label={t("Public Communications")}
                    labels={{ on: t("Public communications via likes and comments."), off: t("Private communications via chat") }}
                    labelPosition="outside"
                    name="mode"
                    on={1}
                    off={0}
                    useQuery={useQuery}
                />
            </RowInfo>

            <RowInfo help={(
                <HelpModal title={t("Delete Judge")}>
                    <p>{t(`Remove this judge from the challenge.`)}</p>
                    <ul>
                        <li>{t(`Deleting a judge removes its avatar, name, instructions, active status, and communication settings.`)}</li>
                        <li>{t(`Use this when AI judging should no longer apply to this challenge.`)}</li>
                    </ul>
                </HelpModal>
            )}>
                <Col size={12}>
                    <ConfirmationModal
                        title={t("Delete Judge")}
                        body={t("Are you sure you want to delete this judge? This action cannot be undone.")}
                        confirmLabel={t("Delete Judge")}
                        confirmAction={deleteJudge}
                        isLoading={isDeleting}
                    >
                        <Button className="btn--flat" color="warning" disabled={isDeleting}>
                            {t("Delete Judge")} {isDeleting && <Spinner size="0.875rem" />}
                        </Button>
                    </ConfirmationModal>
                </Col>
            </RowInfo>
            
        </>
    )
}

const JudgingSection = ({ challengeId: taskId }) => {
    const { t } = useTranslation();
    const judgeQuery = useJudgeQuery({ taskId })
    const { data, isFetching, error } = judgeQuery
    const { mutateAsync: create, isLoading: isCreating } = useCreateJudgeMutation({ taskId })
    const { judges = [] } = data || {}
    const judge = useMemo(() => {
        return judges.find(j => j.task_id === taskId)
    }, [taskId, judges])

    if (error) {
        return (
            <Error 
                title={t("Error loading judges")} 
                message={error.message} 
                button={<Button color="primary" onClick={() => judgeQuery.refetch()}>{t('Retry')}</Button>} 
                height="auto"
                showLogo={false}
                size="sm"
            />
        )
    }
    return (
        <Loadable loading={isFetching && !judge}>
            {
                judge
                    ? <JudgeItem taskId={taskId} id={judge.id} judgeQuery={judgeQuery} />
                    : (
                        <RowInfo help={(
                            <HelpModal title={t("Add Judge")}>
                                <p>{t(`Add an AI judge to review submissions for this challenge.`)}</p>
                                <ul>
                                    <li>{t(`The judge can use your instructions to decide whether submissions are correct, score quality, adjust points, and leave comments.`)}</li>
                                    <li>{t(`After adding a judge, configure its name, avatar, instructions, and communication settings before relying on it for live submissions.`)}</li>
                                </ul>
                            </HelpModal>
                        )}>
                            <Col size={12}>
                                <Button
                                    data-testid="judges__add-new"
                                    size="lg"
                                    className="btn--flat btn--flat-sm"
                                    color="primary"
                                    onClick={create}
                                    disabled={isCreating}
                                >
                                    {t('Add Judge')} {isCreating && <Spinner size="0.875rem" />}
                                </Button>
                            </Col>
                        </RowInfo>
                    )
            }
        </Loadable>
    )
}

/** @deprecated Use NubImageUpload for builder image upload controls. */
export default JudgingSection;
